import {
  type NumberFormat,
  NumberFormats,
  NumberSeparators
} from '@databases/types/database';

const MINUS = '\u2212';

export function formatNumber(
  value: string | undefined,
  numberFormat: NumberFormat
): string {
  const {
    minimumFractionDigits,
    maximumFractionDigits,
    decimalPlaces,
    decimalSeparator,
    thousandsSeparator
  } = numberFormat;
  let formattedValue = extractNumber(value);

  if (!formattedValue) {
    return '';
  }

  if (numberFormat.format === NumberFormats.PERCENT) {
    const numValue = parseFloat(formattedValue);
    if (!isNaN(numValue)) {
      const percentValue = parseFloat((numValue * 100).toFixed(10));
      formattedValue = String(percentValue);
    }
  }

  let { prefix, suffix } = numberFormat;
  let integerValue = formattedValue?.split(NumberSeparators.DOT)[0];
  let decimalValue = formattedValue?.split(NumberSeparators.DOT)[1] ?? '';

  integerValue = integerValue.replace(/^-/, MINUS);

  if (
    prefix &&
    (numberFormat.format === NumberFormats.CURRENCY ||
      numberFormat.format === NumberFormats.CUSTOM)
  ) {
    prefix = `${prefix}${NumberSeparators.WHITESPACE}`;
  } else {
    prefix = '';
  }

  if (
    suffix &&
    (numberFormat.format === NumberFormats.PERCENT ||
      numberFormat.format === NumberFormats.CUSTOM)
  ) {
    suffix = `${NumberSeparators.WHITESPACE}${suffix}`;
  } else {
    suffix = '';
  }
  if (decimalPlaces !== undefined) {
    const places = decimalPlaces;

    const characterCount = decimalValue?.length || 0;
    const deltaCount = characterCount < places ? places - characterCount : 0;
    decimalValue = `${decimalValue}${'0'.repeat(deltaCount)}`;

    decimalValue = decimalValue?.slice(0, places);
  } else {
    if (minimumFractionDigits !== undefined) {
      const characterCount = decimalValue?.length || 0;
      const deltaCount =
        characterCount < minimumFractionDigits
          ? minimumFractionDigits - characterCount
          : 0;
      decimalValue = `${decimalValue}${'0'.repeat(deltaCount)}`;
    }
    if (maximumFractionDigits !== undefined) {
      decimalValue = decimalValue?.slice(0, maximumFractionDigits);
    }
  }

  if (thousandsSeparator) {
    integerValue = integerValue.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
  }

  const combinedValue = `${integerValue}${
    decimalValue ? `${decimalSeparator}${decimalValue}` : ''
  }`;

  return `${prefix}${combinedValue}${suffix}`;
}

/**
 * Finds the first number in the given text, useful when converting a text field into a number field.
 */
export function extractNumber(text: string | undefined): string {
  const dash = '-';
  const m = text?.match(/-?[0-9]*[.,]?[0-9]+/);

  let extracted = '';

  if (m) {
    extracted = m[0].replace(NumberSeparators.COMMA, NumberSeparators.DOT);
    const startsWithSeparator = extracted.startsWith(NumberSeparators.DOT);
    const startsWithNegativeNumberAndSeparator = extracted.startsWith(
      `${dash}${NumberSeparators.DOT}`
    );
    extracted = startsWithSeparator
      ? extracted.replace(NumberSeparators.DOT, `0${NumberSeparators.DOT}`)
      : startsWithNegativeNumberAndSeparator
        ? extracted.replace(
            `${dash}${NumberSeparators.DOT}`,
            `${dash}0${NumberSeparators.DOT}`
          )
        : extracted;
  }

  return extracted;
}

/**
 * Compiles locale and field settings for repeated read-only number rendering.
 */
export function createNumberDisplayFormatter(
  settings: NumberFormat,
  locale: string
): (value: string) => string {
  const numberFormat = Object.freeze({ ...settings });
  switch (numberFormat.format) {
    case NumberFormats.CUSTOM:
    case NumberFormats.CURRENCY:
      return (value) => formatNumber(value, numberFormat);
    case NumberFormats.NUMBER:
      return (value) => {
        try {
          return formatNumber(value, numberFormat);
        } catch {
          return '';
        }
      };
    case NumberFormats.PERCENT: {
      const percentFormat = Object.freeze({ ...numberFormat, suffix: '%' });
      return (value) => formatNumber(value, percentFormat);
    }
    case NumberFormats.DOLLAR: {
      const formatter = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD'
      });
      return (value) => formatter.format(Number(value));
    }
    case NumberFormats.EURO: {
      const formatter = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR'
      });
      return (value) => formatter.format(Number(value));
    }
  }
}
